import { DebouncedAccessor, useDebouncedInput } from '@/lib/common-utils.js';
import { isAirAdmin, WithAuthRole } from '@airreading/common';
import {
  getRouteApi,
  redirect,
  type AnyRoute,
  type RegisteredRouter,
  type RouteById,
  type RouteIds,
  type ToOptions,
} from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * The default route tree.
 */
export type RegisteredRouteTree = RegisteredRouter['routeTree'];

export type RegisteredToOptions = ToOptions<RegisteredRouter>;

/**
 * Look up the query string schema of the route matching the given route ID.
 */
export type FullSearchSchemaOfRouteId<TId extends RouteIds<TRouteTree>, TRouteTree extends AnyRoute> = RouteById<
  TRouteTree,
  TId
>['types']['fullSearchSchema'];

/**
 * Ensure that the given route ID has all the keys in the given query string schema.
 */
export type HasAllQueryKeys<TId extends RouteIds<TRouteTree>, TRouteTree extends AnyRoute, Keys extends string> = [
  Keys,
] extends [keyof FullSearchSchemaOfRouteId<TId, TRouteTree>]
  ? true
  : false;

/**
 * Ensures that the given type includes all keys and value types from the constraint.
 */
export type HasAllKeysAndTypes<T extends object, Constraint extends object> =
  Required<T> extends Required<Constraint> ? true : false;

/**
 * Filters all route IDs whose search schema structurally satisfies the given constraint.
 * The constraint type must include all required keys and their types exactly.
 */
export type RoutesWithValidConstraint<Constraint extends object> = {
  [TId in RouteIds<RegisteredRouteTree>]: HasAllKeysAndTypes<
    FullSearchSchemaOfRouteId<TId, RegisteredRouteTree>,
    Constraint
  > extends true
    ? TId
    : never;
}[RouteIds<RegisteredRouteTree>];

/**
 * A pair of `[data, setter]` like the one returned by `useState`.
 */
export type Accessor<Data> = [value: Data, setter: (value: Data) => void];

/**
 * Use the state of the query string in the given route ID's route.
 *
 * @param routeId a valid route ID. Usually `Route.id`
 * @param queryStringKey a key present in the route's search validation schema
 * @returns a `[data, setter]` pair like `useState`.
 */
export function useNavigateSearchState<
  TId extends RouteIds<TRouteTree>,
  TRouteTree extends AnyRoute = RegisteredRouteTree,
  TFullSearchSchema extends object = FullSearchSchemaOfRouteId<TId, TRouteTree>,
  QueryStringKey extends keyof TFullSearchSchema = keyof TFullSearchSchema,
  QueryStringValue extends TFullSearchSchema[QueryStringKey] = TFullSearchSchema[QueryStringKey],
>(routeId: TId, queryStringKey: QueryStringKey): Accessor<QueryStringValue> {
  const route = getRouteApi(routeId);
  const navigate = route.useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value = (route.useSearch() as any)[queryStringKey];
  const setter = useCallback(
    (newValue: QueryStringValue) => {
      navigate({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: (prev: any) => ({ ...prev, [queryStringKey]: newValue }) as any,
      });
    },
    [navigate, queryStringKey],
  );
  return [value, setter];
}

export function useDebouncedSearchState<
  TId extends RouteIds<TRouteTree>,
  TRouteTree extends AnyRoute = RegisteredRouteTree,
  TFullSearchSchema extends object = FullSearchSchemaOfRouteId<TId, TRouteTree>,
  QueryStringKey extends keyof TFullSearchSchema = keyof TFullSearchSchema,
  QueryStringValue extends TFullSearchSchema[QueryStringKey] = TFullSearchSchema[QueryStringKey],
>(routeId: TId, queryStringKey: QueryStringKey): DebouncedAccessor<QueryStringValue | undefined> {
  const [query, didChange] = useNavigateSearchState(routeId, queryStringKey);
  return useDebouncedInput(query, didChange);
}

export type GuardContext = {
  context: {
    actingUser?: WithAuthRole;
  };
};

export interface ValidateRouteRoleOptions {
  /** Where to send a user who fails the check. Defaults to `/403`. */
  redirectTo?: string;
  /**
   * Whether any air admin is allowed through regardless of `predicate`. True by
   * default, which is what lets admins onto coach- and student-facing pages —
   * e.g. `/lessons/$lessonId` is guarded by `isCoach`, yet it is where admins
   * edit lessons.
   *
   * Set this to `false` for routes that must be *narrower* than admin. The
   * default cannot express those, because `isAirAdmin` is also true for an
   * `air_limited_admin` and ignores role status — so passing a stricter
   * predicate alone (e.g. `isFullAirAdmin`) would never get to deny anyone.
   * Disabling the allowance makes `predicate` authoritative; supply one, since
   * without it nobody passes.
   */
  allowAnyAirAdmin?: boolean;
}

const userHasAccess = (
  actingUser: WithAuthRole,
  predict?: (user: WithAuthRole) => boolean,
  allowAnyAirAdmin: boolean = true,
) => {
  return (allowAnyAirAdmin && isAirAdmin(actingUser)) || predict?.(actingUser);
};

/**
 * Checks if the user is allowed to access the route.
 * Invoke this function in the component of the route using `beforeLoad` in `createFileRoute`.
 *
 * @param predicate check if the user is allowed to access the route. If not provided, only admin users are allowed
 * @param options see {@link ValidateRouteRoleOptions}
 * @example
 * ```tsx
 * export const Route = createFileRoute('ADMIN/ROUTE')({
 *   beforeLoad: validateRouteRole(isAirAdmin), // OR validateRouteRole(),
 *   component: MyAdminComponent,
 * });
 *
 * export const Route = createFileRoute('COACH/ROUTE')({
 *   beforeLoad: validateRouteRole(isAirCoach),
 *   component: MyCoachComponent,
 * });
 *
 * export const Route = createFileRoute('FULL/ADMIN/ONLY/ROUTE')({
 *   beforeLoad: validateRouteRole(canPurgeData, { allowAnyAirAdmin: false }),
 *   component: MyFullAdminOnlyComponent,
 * });
 * ```
 */
export const validateRouteRole = (
  predicate?: (user: WithAuthRole) => boolean,
  // TODO
  // The implementation of air_limited_admin was overloading isAirAdmin to
  // keep the scope down, but we should rename some of those functions and
  // then update some of the names here.
  { redirectTo = '/403', allowAnyAirAdmin = true }: ValidateRouteRoleOptions = {},
) => {
  return ({ context }: GuardContext) => {
    const { actingUser } = context;

    if (!actingUser) {
      return;
    }

    if (!userHasAccess(actingUser, predicate, allowAnyAirAdmin)) {
      throw redirect({ to: redirectTo });
    }
  };
};
